import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import ShipmentList from './ShipmentList'
import ShipmentDetails from './ShipmentDetails'
import BusinessInfoSetup from './BusinessInfoSetup'
import { apiService } from '../services/apiService'
import { passwordCache } from '../utils/passwordCache'
import type { User, Shipment } from '../types'

interface DashboardProps {
  user: User
  onBusinessInfoUpdate?: () => void
}

interface PaginationInfo {
  currentPage: number
  lastPage: number
  perPage: number
  total: number
}

// Secondary client info
interface SecondaryClient {
  id: number
  name: string
  email: string
}

export default function Dashboard({ user, onBusinessInfoUpdate }: DashboardProps) {
  const navigate = useNavigate()
  const [shipments, setShipments] = useState<Shipment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [pagination, setPagination] = useState<PaginationInfo | null>(null)
  const [businessInfoSetup, setBusinessInfoSetup] = useState<boolean | null>(null)
  const [businessInfoIncomplete, setBusinessInfoIncomplete] = useState<boolean>(false)
  const [showCombinedInvoiceDialog, setShowCombinedInvoiceDialog] = useState(false)
  const [selectedShipmentsForInvoice, setSelectedShipmentsForInvoice] = useState<Shipment[]>([])
  const [combinedInvoiceLoading, setCombinedInvoiceLoading] = useState(false)
  const [showMergedEstimateDialog, setShowMergedEstimateDialog] = useState(false)
  const [selectedShipmentsForEstimate, setSelectedShipmentsForEstimate] = useState<Shipment[]>([])
  const [mergedEstimateLoading, setMergedEstimateLoading] = useState(false)
  const [mergedEstimateData, setMergedEstimateData] = useState<any>(null)

  // Multi-client invoice state (staff only)
  const [isStaff, setIsStaff] = useState(false)
  const [secondaryClientEmail, setSecondaryClientEmail] = useState('')
  const [secondaryClient, setSecondaryClient] = useState<SecondaryClient | null>(null)
  const [secondaryShipments, setSecondaryShipments] = useState<Shipment[]>([])
  const [secondaryClientLoading, setSecondaryClientLoading] = useState(false)
  const [secondaryClientError, setSecondaryClientError] = useState<string | null>(null)
  const [selectedSecondaryShipmentsForInvoice, setSelectedSecondaryShipmentsForInvoice] = useState<Shipment[]>([])
  const [showMultiClientInvoiceDialog, setShowMultiClientInvoiceDialog] = useState(false)
  const [primaryClientInfo, setPrimaryClientInfo] = useState<{ id: number; name: string } | null>(null)

  useEffect(() => {
    // Add a small delay to ensure auth context is fully initialized
    const initializeDashboard = async () => {
      const token = localStorage.getItem('yyz-cbp-token')
      if (!token) {
        console.warn('⚠️ No token found, dashboard should not load')
        return
      }

      // Check if user is staff
      const staffUser = localStorage.getItem('staffUser')
      if (staffUser) {
        setIsStaff(true)
        console.log('📊 Staff user detected')
      }

      console.log('📊 Dashboard initializing with token present')
      loadShipments(1)
      checkBusinessInfo()
      loadPrimaryClientInfo()
    }

    // Small delay to ensure auth context is ready
    setTimeout(initializeDashboard, 100)
  }, [])

  const checkBusinessInfo = async () => {
    try {
      const response = await apiService.getClientBusinessInfo()
      const hasBusinessInfo = response.success && response.data !== null
      setBusinessInfoSetup(hasBusinessInfo)

      // Check if business info is incomplete (missing phone number or business type)
      if (hasBusinessInfo && response.data) {
        const isIncomplete = !response.data.phone_number || !response.data.business_type
        setBusinessInfoIncomplete(isIncomplete)
      } else {
        setBusinessInfoIncomplete(false)
      }
    } catch (error) {
      console.error('Error checking business info:', error)
      setBusinessInfoSetup(false)
      setBusinessInfoIncomplete(false)
    }
  }

  const loadPrimaryClientInfo = async () => {
    try {
      const response = await apiService.getClientBusinessInfo()
      if (response.success && response.data) {
        setPrimaryClientInfo({
          id: response.data.client_id,
          name: response.data.legal_name || user?.companyName || 'Primary Client'
        })
      }
    } catch (error) {
      console.error('Error loading primary client info:', error)
    }
  }

  // Staff-only: Load secondary client by email
  const loadSecondaryClient = async () => {
    if (!secondaryClientEmail.trim()) {
      setSecondaryClientError('Please enter an email address')
      return
    }

    try {
      setSecondaryClientLoading(true)
      setSecondaryClientError(null)

      // Look up client by email
      const clientResponse = await apiService.lookupClientByEmail(secondaryClientEmail.trim())

      if (!clientResponse.success) {
        throw new Error(clientResponse.error || 'Client not found')
      }

      const client = clientResponse.client
      if (!client) {
        throw new Error('No client found with this email')
      }

      setSecondaryClient(client)

      // Load their shipments
      const shipmentsResponse = await apiService.getOtherClientShipments(client.id, 1)

      if (shipmentsResponse.success) {
        setSecondaryShipments(shipmentsResponse.data || [])
        console.log(`✅ Loaded ${shipmentsResponse.data?.length || 0} shipments for secondary client: ${client.name}`)
      } else {
        throw new Error(shipmentsResponse.error || 'Failed to load secondary client shipments')
      }
    } catch (error) {
      console.error('Error loading secondary client:', error)
      setSecondaryClientError((error as Error).message)
      setSecondaryClient(null)
      setSecondaryShipments([])
    } finally {
      setSecondaryClientLoading(false)
    }
  }

  // Staff-only: Clear secondary client
  const clearSecondaryClient = () => {
    setSecondaryClient(null)
    setSecondaryShipments([])
    setSecondaryClientEmail('')
    setSecondaryClientError(null)
  }

  // Handler for multi-client invoice generation
  const handleMultiClientInvoiceGenerate = (primaryShipments: Shipment[], secondaryShipmentsSelected: Shipment[]) => {
    setSelectedShipmentsForInvoice(primaryShipments)
    setSelectedSecondaryShipmentsForInvoice(secondaryShipmentsSelected)
    setShowMultiClientInvoiceDialog(true)
  }

  const loadShipments = async (page: number = 1) => {
    try {
      setError(null)
      setLoading(true)
      
      console.log('🚢 Loading shipments, page:', page)
      const response = await apiService.getShipments(page)
      
      if (response.success) {
        setShipments(response.data || [])
        setPagination(response.pagination || null)
      } else {
        setError(response.error || 'Failed to load shipments')
      }
    } catch (err) {
      console.error('Failed to load shipments:', err)
      setError('Failed to connect to server. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleShipmentSelect = (shipment: Shipment) => {
    navigate(`/dashboard/shipment/${shipment.id}`)
  }

  const handleMultipleInvoiceGenerate = (selectedShipments: Shipment[]) => {
    setSelectedShipmentsForInvoice(selectedShipments)
    setShowCombinedInvoiceDialog(true)
  }

  const handleMergedEstimateClearance = (selectedShipments: Shipment[]) => {
    setSelectedShipmentsForEstimate(selectedShipments)
    setShowMergedEstimateDialog(true)
  }

  const handleCombinedInvoiceGenerate = async (
    password: string,
    ltlType: 'direct_amazon' | 'pattern_middle_mile' | 'ups_buffalo',
    brokerType: 'clearit' | 'acb' | 'jas',
    fcInfo?: any
  ) => {
    try {
      setCombinedInvoiceLoading(true)
      
      // Fetch items for each selected shipment
      const shipmentsWithItems = await Promise.all(
        selectedShipmentsForInvoice.map(async (shipment) => {
          const itemsResponse = await apiService.getShipmentItems(shipment.id)
          return {
            ...shipment,
            items: itemsResponse.data || []
          }
        })
      )

      // Enrich items with saved ASIN data before validation
      for (const shipment of shipmentsWithItems) {
        for (const item of shipment.items) {
          if (item.asin) {
            try {
              const asinResponse = await apiService.getClientASINData(item.asin)
              if (asinResponse.success && asinResponse.data?.asinData) {
                const asinData = asinResponse.data.asinData
                if (asinData.cogs_cad && (!item.declaredValue || item.declaredValue <= 0)) {
                  item.declaredValue = asinData.cogs_cad
                  item.currency = 'CAD'
                }
                if (asinData.country_of_origin && !item.countryOfOrigin) {
                  item.countryOfOrigin = asinData.country_of_origin
                }
                if (asinData.hts_code && !item.htsCode) {
                  item.htsCode = asinData.hts_code
                }
              }
            } catch (error) {
              console.error(`Error fetching ASIN data for ${item.asin}:`, error)
            }
          }
        }
      }

      // Validate all items have required fields: COGS, Country of Origin, HTS Code
      const itemsWithMissingFields: string[] = []
      for (const shipment of shipmentsWithItems) {
        for (const item of shipment.items) {
          const missingFields: string[] = []

          if (!item.declaredValue || item.declaredValue <= 0) {
            missingFields.push('COGS')
          }
          if (!item.countryOfOrigin) {
            missingFields.push('Country of Origin')
          }
          if (!item.htsCode || item.htsCode.replace(/\D/g, '').length !== 10) {
            missingFields.push('HTS Code (10 digits)')
          }

          if (missingFields.length > 0) {
            const itemName = item.asin || item.description?.substring(0, 30) || 'Unknown item'
            itemsWithMissingFields.push(`• ${shipment.reference} - ${itemName}: Missing ${missingFields.join(', ')}`)
          }
        }
      }

      if (itemsWithMissingFields.length > 0) {
        setCombinedInvoiceLoading(false)
        alert(`Cannot generate invoice. The following items are missing required fields:\n\n${itemsWithMissingFields.join('\n')}`)
        return
      }

      const response = await apiService.generateCombinedInvoice(
        shipmentsWithItems,
        password,
        ltlType,
        brokerType,
        fcInfo
      )
      
      if (response.success && response.fileData) {
        // Convert base64 to blob and download
        const byteCharacters = atob(response.fileData)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: 'application/pdf' })
        
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = response.filename || 'combined-commercial-invoice.pdf'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
        
        setShowCombinedInvoiceDialog(false)
        alert(`Combined invoice generated successfully! References: ${response.combinedShipments?.join(', ')}`)
      } else {
        throw new Error(response.error || 'Failed to generate combined invoice')
      }
    } catch (error) {
      console.error('Combined invoice generation error:', error)
      alert('Failed to generate combined invoice: ' + (error as Error).message)
    } finally {
      setCombinedInvoiceLoading(false)
    }
  }

  const handleGenerateMergedEstimate = async () => {
    try {
      setMergedEstimateLoading(true)
      
      const shipmentIds = selectedShipmentsForEstimate.map(s => s.id)
      const response = await apiService.generateMergedEstimateClearance(shipmentIds)
      
      if (response.success) {
        setMergedEstimateData(response.data)
        alert('Merged estimate clearance generated successfully!')
      } else {
        throw new Error(response.error || 'Failed to generate merged estimate clearance')
      }
    } catch (error) {
      console.error('Merged estimate generation error:', error)
      alert('Failed to generate merged estimate clearance: ' + (error as Error).message)
    } finally {
      setMergedEstimateLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div>Loading your shipments...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card">
        <div className="error">
          {error}
        </div>
        <button 
          className="button" 
          onClick={() => loadShipments(1)}
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="dashboard">
      {/* Business Info Notification */}
      {businessInfoSetup === false && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '4px',
          color: '#856404',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <strong>Business Information Required:</strong> Please set up your business information for shipment management.
          </div>
          <button
            onClick={() => onBusinessInfoUpdate && onBusinessInfoUpdate()}
            style={{
              padding: '6px 12px',
              border: 'none',
              backgroundColor: '#007bff',
              color: '#fff',
              borderRadius: '4px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Set Up Now
          </button>
        </div>
      )}

      {/* Business Info Incomplete Notification */}
      {businessInfoSetup === true && businessInfoIncomplete && (
        <div style={{
          padding: '12px 16px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '4px',
          color: '#856404',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <strong>Business Information Update Needed:</strong> Please add your phone number and business type to complete your profile.
          </div>
          <button
            onClick={() => onBusinessInfoUpdate && onBusinessInfoUpdate()}
            style={{
              padding: '6px 12px',
              border: 'none',
              backgroundColor: '#007bff',
              color: '#fff',
              borderRadius: '4px',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            Update Now
          </button>
        </div>
      )}

      {/* Shipment Process Information Banner */}
      {(
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <div style={{
              fontSize: '24px',
              marginTop: '2px'
            }}>
              📋
            </div>
            <div>
              <h3 style={{
                margin: '0 0 8px 0',
                fontSize: '18px',
                fontWeight: '600',
                color: '#0c4a6e'
              }}>
                Ready to Process Your US Shipments?
              </h3>
              <p style={{
                margin: '0 0 12px 0',
                fontSize: '14px',
                color: '#0369a1',
                lineHeight: '1.5'
              }}>
                To process any US shipments (both informal and formal clearance), please ensure each outbound shipment contains:
              </p>
              <ul style={{
                margin: '0 0 12px 0',
                paddingLeft: '20px',
                fontSize: '14px',
                color: '#0369a1',
                lineHeight: '1.6'
              }}>
                <li><strong>Country of Origin</strong> - Where each product was manufactured</li>
                <li><strong>HTS Classification</strong> - US Harmonized Tariff Schedule codes for duty calculation</li>
                <li><strong>Manufacturer Information</strong> - Complete manufacturer name and address</li>
                <li><strong>Business Information</strong> - Your company details for customs documentation</li>
              </ul>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '13px',
                color: '#075985',
                fontStyle: 'italic',
                marginBottom: '16px'
              }}>
                <span>💡</span>
                <span>Use our AI tools to automatically classify HTS codes and find manufacturer addresses!</span>
              </div>
              
              {/* Formal Entry Notice */}
              <div style={{
                backgroundColor: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '6px',
                padding: '12px',
                fontSize: '14px',
                color: '#92400e',
                marginBottom: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <span style={{ fontSize: '16px', marginTop: '2px' }}>🏢</span>
                  <div>
                    <strong>Formal Entries (Shipments &gt; $2,500 USD COGS):</strong>
                    <br />
                    <a 
                      href="mailto:hello@yyzprep.ca?subject=Requesting Partnered Broker Invitation"
                      style={{
                        color: '#92400e',
                        textDecoration: 'underline'
                      }}
                    >
                      Email us at hello@yyzprep.ca
                    </a> to set up your custom brokerage account with our partnered broker.
                  </div>
                </div>
              </div>

              {/* Pattern Middle Mile Notice */}
              <div style={{
                backgroundColor: '#f0fdf4',
                border: '1px solid #16a34a',
                borderRadius: '6px',
                padding: '12px',
                fontSize: '14px',
                color: '#166534'
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <span style={{ fontSize: '16px', marginTop: '2px' }}>🚛</span>
                  <div>
                    <strong>Pattern Middle Mile Optimization:</strong>
                    <br />
                    Where beneficial, we utilize Pattern Middle Mile to achieve minimal or $0 placement fees while clearing everything under a single customs entry. 
                    <a 
                      href="https://yyzprep.ca/guides/pattern-middle-mile-guide-pay-0-placement-fees"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#166534',
                        textDecoration: 'underline',
                        marginLeft: '4px'
                      }}
                    >
                      Learn more about our middle mile strategy →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <>
          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">Your Outbound Shipments</h2>
              <div className="flex gap-2">
                <button 
                  className="button primary" 
                  onClick={() => navigate('/manual-invoice')}
                >
                  Manual Invoice Generator
                </button>
                <button 
                  className="button secondary" 
                  onClick={() => loadShipments(pagination?.currentPage || 1)}
                >
                  Refresh
                </button>
              </div>
            </div>
            
            <div style={{ color: '#666', marginBottom: '20px' }}>
              Select a shipment to manage items, classify products, and estimate duty rates. Or use the Manual Invoice Generator to create custom invoices with AI-powered item parsing.
            </div>
          </div>

          {/* Secondary Client Input - Available to all authenticated users */}
          <div style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#495057' }}>
              Multi-Client Invoice Merging
            </h3>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '250px' }}>
                  <label style={{ display: 'block', fontSize: '14px', marginBottom: '4px', color: '#666' }}>
                    Secondary Client Email
                  </label>
                  <input
                    type="email"
                    value={secondaryClientEmail}
                    onChange={(e) => setSecondaryClientEmail(e.target.value)}
                    placeholder="Enter secondary client's email"
                    disabled={secondaryClientLoading || !!secondaryClient}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ced4da',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !secondaryClient) {
                        loadSecondaryClient()
                      }
                    }}
                  />
                </div>
                {!secondaryClient ? (
                  <button
                    onClick={loadSecondaryClient}
                    disabled={secondaryClientLoading || !secondaryClientEmail.trim()}
                    style={{
                      padding: '8px 20px',
                      border: 'none',
                      backgroundColor: secondaryClientLoading ? '#6c757d' : '#17a2b8',
                      color: '#fff',
                      borderRadius: '4px',
                      cursor: secondaryClientLoading || !secondaryClientEmail.trim() ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    {secondaryClientLoading ? 'Loading...' : 'Load Client'}
                  </button>
                ) : (
                  <button
                    onClick={clearSecondaryClient}
                    style={{
                      padding: '8px 20px',
                      border: '1px solid #dc3545',
                      backgroundColor: '#fff',
                      color: '#dc3545',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Remove Secondary Client
                  </button>
                )}
              </div>

              {secondaryClientError && (
                <div style={{
                  color: '#dc3545',
                  fontSize: '14px',
                  marginTop: '8px'
                }}>
                  {secondaryClientError}
                </div>
              )}

              {secondaryClient && (
                <div style={{
                  backgroundColor: '#e3f2fd',
                  padding: '12px',
                  borderRadius: '6px',
                  marginTop: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <span style={{
                    backgroundColor: '#2196f3',
                    color: '#fff',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '12px'
                  }}>
                    Secondary
                  </span>
                  <div>
                    <strong>{secondaryClient.name}</strong>
                    <span style={{ color: '#666', marginLeft: '8px' }}>({secondaryClient.email})</span>
                  </div>
                  <div style={{ marginLeft: 'auto', color: '#0d6efd' }}>
                    {secondaryShipments.length} shipments loaded
                  </div>
                </div>
              )}
          </div>

          <ShipmentList
            shipments={shipments}
            onShipmentSelect={handleShipmentSelect}
            pagination={pagination}
            onPageChange={loadShipments}
            loading={loading}
            onMultipleInvoiceGenerate={secondaryClient ? undefined : handleMultipleInvoiceGenerate}
            onMergedEstimateClearance={secondaryClient ? undefined : handleMergedEstimateClearance}
            // Multi-client support
            secondaryShipments={secondaryClient ? secondaryShipments : undefined}
            secondaryClientName={secondaryClient?.name}
            primaryClientName={primaryClientInfo?.name}
            onMultiClientInvoiceGenerate={secondaryClient ? handleMultiClientInvoiceGenerate : undefined}
          />

          {shipments.length === 0 && (
            <div className="card text-center">
              <h3>No Shipments Found</h3>
              <p style={{ color: '#666', margin: '10px 0' }}>
                You don't have any outbound shipments in our system yet.
              </p>
              <p style={{ color: '#666', fontSize: '14px' }}>
                Please contact YYZ Prep if you believe this is an error.
              </p>
            </div>
          )}
        </>
      
      {/* Combined Invoice Generation Dialog */}
      {showCombinedInvoiceDialog && (
        <CombinedInvoiceDialog
          selectedShipments={selectedShipmentsForInvoice}
          loading={combinedInvoiceLoading}
          onGenerate={handleCombinedInvoiceGenerate}
          onCancel={() => setShowCombinedInvoiceDialog(false)}
        />
      )}

      {/* Merged Estimate Clearance Dialog */}
      {showMergedEstimateDialog && (
        <MergedEstimateDialog
          selectedShipments={selectedShipmentsForEstimate}
          loading={mergedEstimateLoading}
          estimateData={mergedEstimateData}
          onGenerate={handleGenerateMergedEstimate}
          onCancel={() => {
            setShowMergedEstimateDialog(false)
            setMergedEstimateData(null)
          }}
        />
      )}

      {/* Multi-Client Combined Invoice Dialog (Staff Only) */}
      {showMultiClientInvoiceDialog && primaryClientInfo && secondaryClient && (
        <MultiClientInvoiceDialog
          primaryClient={{ id: primaryClientInfo.id, name: primaryClientInfo.name }}
          secondaryClient={secondaryClient}
          primaryShipments={selectedShipmentsForInvoice}
          secondaryShipments={selectedSecondaryShipmentsForInvoice}
          loading={combinedInvoiceLoading}
          onGenerate={async (params) => {
            try {
              setCombinedInvoiceLoading(true)

              // Fetch items for primary shipments
              const primaryShipmentsWithItems = await Promise.all(
                selectedShipmentsForInvoice.map(async (shipment) => {
                  const itemsResponse = await apiService.getShipmentItems(shipment.id)
                  return {
                    ...shipment,
                    items: itemsResponse.data || []
                  }
                })
              )

              // Fetch items for secondary shipments
              const secondaryShipmentsWithItems = await Promise.all(
                selectedSecondaryShipmentsForInvoice.map(async (shipment) => {
                  const itemsResponse = await apiService.getOtherClientShipmentItems(secondaryClient.id, shipment.id)
                  return {
                    ...shipment,
                    items: itemsResponse.data || []
                  }
                })
              )

              // Enrich items with saved ASIN data before validation
              const enrichShipments = async (shipments: any[], clientId?: string) => {
                for (const shipment of shipments) {
                  for (const item of shipment.items) {
                    if (item.asin) {
                      try {
                        const asinResponse = await apiService.getClientASINData(item.asin, clientId)
                        if (asinResponse.success && asinResponse.data?.asinData) {
                          const asinData = asinResponse.data.asinData
                          if (asinData.cogs_cad && (!item.declaredValue || item.declaredValue <= 0)) {
                            item.declaredValue = asinData.cogs_cad
                            item.currency = 'CAD'
                          }
                          if (asinData.country_of_origin && !item.countryOfOrigin) {
                            item.countryOfOrigin = asinData.country_of_origin
                          }
                          if (asinData.hts_code && !item.htsCode) {
                            item.htsCode = asinData.hts_code
                          }
                        }
                      } catch (error) {
                        console.error(`Error fetching ASIN data for ${item.asin}:`, error)
                      }
                    }
                  }
                }
              }
              await enrichShipments(primaryShipmentsWithItems)
              await enrichShipments(secondaryShipmentsWithItems, secondaryClient.id.toString())

              // Validate all items have required fields: COGS, Country of Origin, HTS Code
              const itemsWithMissingFields: string[] = []
              const allShipments = [...primaryShipmentsWithItems, ...secondaryShipmentsWithItems]
              for (const shipment of allShipments) {
                for (const item of shipment.items) {
                  const missingFields: string[] = []

                  if (!item.declaredValue || item.declaredValue <= 0) {
                    missingFields.push('COGS')
                  }
                  if (!item.countryOfOrigin) {
                    missingFields.push('Country of Origin')
                  }
                  if (!item.htsCode || item.htsCode.replace(/\D/g, '').length !== 10) {
                    missingFields.push('HTS Code (10 digits)')
                  }

                  if (missingFields.length > 0) {
                    const itemName = item.asin || item.description?.substring(0, 30) || 'Unknown item'
                    itemsWithMissingFields.push(`• ${shipment.reference} - ${itemName}: Missing ${missingFields.join(', ')}`)
                  }
                }
              }

              if (itemsWithMissingFields.length > 0) {
                setCombinedInvoiceLoading(false)
                alert(`Cannot generate invoice. The following items are missing required fields:\n\n${itemsWithMissingFields.join('\n')}`)
                return
              }

              // Generate the multi-client combined invoice
              const response = await apiService.generateMultiClientCombinedInvoice({
                primaryClientId: primaryClientInfo.id,
                secondaryClientId: secondaryClient.id,
                primaryShipments: primaryShipmentsWithItems,
                secondaryShipments: secondaryShipmentsWithItems,
                importerClientId: params.importerClientId,
                password: params.password,
                ltlType: params.ltlType,
                brokerType: params.brokerType,
                fcInfo: params.fcInfo
              })

              if (response.success && response.fileData) {
                // Convert base64 to blob and download
                const byteCharacters = atob(response.fileData)
                const byteNumbers = new Array(byteCharacters.length)
                for (let i = 0; i < byteCharacters.length; i++) {
                  byteNumbers[i] = byteCharacters.charCodeAt(i)
                }
                const byteArray = new Uint8Array(byteNumbers)
                const blob = new Blob([byteArray], { type: 'application/pdf' })

                const url = window.URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = url
                link.download = response.filename || 'multi-client-combined-invoice.pdf'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)

                setShowMultiClientInvoiceDialog(false)
                alert(`Multi-client combined invoice generated successfully! References: ${response.combinedShipments?.join(', ')}`)
              } else {
                throw new Error(response.error || 'Failed to generate multi-client combined invoice')
              }
            } catch (error) {
              console.error('Multi-client combined invoice generation error:', error)
              alert('Failed to generate multi-client combined invoice: ' + (error as Error).message)
            } finally {
              setCombinedInvoiceLoading(false)
            }
          }}
          onCancel={() => setShowMultiClientInvoiceDialog(false)}
        />
      )}
    </div>
  )
}

interface CombinedInvoiceDialogProps {
  selectedShipments: Shipment[]
  loading: boolean
  onGenerate: (password: string, ltlType: 'direct_amazon' | 'pattern_middle_mile' | 'ups_buffalo', brokerType: 'clearit' | 'acb' | 'jas', fcInfo?: any) => void
  onCancel: () => void
}

function CombinedInvoiceDialog({ selectedShipments, loading, onGenerate, onCancel }: CombinedInvoiceDialogProps) {
  const [password, setPassword] = useState('')
  const [brokerType, setBrokerType] = useState<'clearit' | 'acb' | 'jas'>('clearit')
  const [ltlType, setLtlType] = useState<'direct_amazon' | 'pattern_middle_mile' | 'ups_buffalo'>('pattern_middle_mile')
  const [fcId, setFcId] = useState('')
  const [fcAddress, setFcAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: ''
  })
  const [cachedMinutes, setCachedMinutes] = useState(0)

  // Load cached password on mount
  useEffect(() => {
    const cached = passwordCache.get()
    if (cached) {
      setPassword(cached)
      setCachedMinutes(passwordCache.getRemainingMinutes())
    }
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!password.trim()) return

    // Cache the password for 2 hours
    passwordCache.set(password)

    const fcInfo = ltlType === 'direct_amazon' && fcId.trim()
      ? { fcId: fcId.trim(), fcAddress }
      : undefined

    onGenerate(password, ltlType, brokerType, fcInfo)
  }
  
  const totalItems = selectedShipments.reduce((sum, s) => sum + (s.itemCount || 0), 0)
  const totalSkus = selectedShipments.reduce((sum, s) => sum + (s.skuCount || 0), 0)
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ padding: '24px' }}>
          <h2 style={{ margin: '0 0 20px 0' }}>Generate Combined Customs Invoice</h2>
          
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '16px',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px' }}>Selected Shipments ({selectedShipments.length})</h3>
            <div style={{ fontSize: '14px', color: '#666' }}>
              <div><strong>References:</strong> {selectedShipments.map(s => s.reference).join(', ')}</div>
              <div style={{ marginTop: '8px' }}>
                <strong>Total:</strong> {totalSkus} SKUs, {totalItems} units
              </div>
            </div>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Broker Information <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                value={brokerType}
                onChange={(e) => setBrokerType(e.target.value as 'clearit' | 'acb' | 'jas')}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="clearit">ClearIT Broker Information</option>
                <option value="acb">ACB Broker Information</option>
                <option value="jas">JAS Forwarding — Lynn E. Reed</option>
              </select>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                LTL Type <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                value={ltlType}
                onChange={(e) => {
                  setLtlType(e.target.value as any)
                  if (e.target.value !== 'direct_amazon') {
                    setFcId('')
                    setFcAddress({ street: '', city: '', state: '', zip: '' })
                  }
                }}
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
              >
                <option value="direct_amazon">Direct to Amazon LTL</option>
                <option value="pattern_middle_mile">Pattern Middle Mile LTL</option>
                <option value="ups_buffalo">UPS Buffalo Warehouse</option>
              </select>
              <div style={{
                fontSize: '12px',
                color: '#666',
                marginTop: '4px'
              }}>
                {ltlType === 'pattern_middle_mile'
                  ? 'Destination: Pattern Middle Mile - Hebron Warehouse, 2250 Progress Dr, Hebron, KY 41048'
                  : ltlType === 'ups_buffalo'
                  ? 'Destination: UPS Warehouse (EIN 58-2480149), 1907 James E Casey Drive, Buffalo, New York 14206, Tel: +1 888-742-5877'
                  : 'Destination: Amazon Fulfillment Center'}
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Password <span style={{ color: 'red' }}>*</span>
                {cachedMinutes > 0 && (
                  <span style={{ fontWeight: 'normal', color: '#28a745', marginLeft: '8px', fontSize: '12px' }}>
                    (cached for {cachedMinutes} min)
                  </span>
                )}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: cachedMinutes > 0 ? '1px solid #28a745' : '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: cachedMinutes > 0 ? '#f8fff8' : '#fff'
                }}
                placeholder="Enter password for invoice generation"
              />
            </div>

            {ltlType === 'direct_amazon' && (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                    FC ID <span style={{ color: 'red' }}>*</span>
                  </label>
                  <input
                    type="text"
                    value={fcId}
                    onChange={(e) => setFcId(e.target.value)}
                    disabled={loading}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '14px'
                    }}
                    placeholder="e.g., DFW1, LAX9"
                  />
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                      Street Address <span style={{ color: 'red' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={fcAddress.street}
                      onChange={(e) => setFcAddress({...fcAddress, street: e.target.value})}
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                      City <span style={{ color: 'red' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={fcAddress.city}
                      onChange={(e) => setFcAddress({...fcAddress, city: e.target.value})}
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                      State <span style={{ color: 'red' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={fcAddress.state}
                      onChange={(e) => setFcAddress({...fcAddress, state: e.target.value})}
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                      ZIP Code <span style={{ color: 'red' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={fcAddress.zip}
                      onChange={(e) => setFcAddress({...fcAddress, zip: e.target.value})}
                      disabled={loading}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              </>
            )}
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  backgroundColor: '#fff',
                  color: '#666',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !password.trim()}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  backgroundColor: loading || !password.trim() ? '#ccc' : '#007bff',
                  color: '#fff',
                  borderRadius: '4px',
                  cursor: loading || !password.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                {loading ? 'Generating...' : 'Generate Combined Invoice'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

interface MergedEstimateDialogProps {
  selectedShipments: Shipment[]
  loading: boolean
  estimateData: any
  onGenerate: () => void
  onCancel: () => void
}

function MergedEstimateDialog({ selectedShipments, loading, estimateData, onGenerate, onCancel }: MergedEstimateDialogProps) {
  const totalItems = selectedShipments.reduce((sum, s) => sum + (s.itemCount || 0), 0)
  const totalSkus = selectedShipments.reduce((sum, s) => sum + (s.skuCount || 0), 0)
  
  const formatCurrencyUSD = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }
  
  const formatCurrencyCAD = (amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount)
  }
  
  const calculateTotal = (fees: any[]) => {
    return fees.reduce((total, fee) => {
      const amount = typeof fee.lineAmount === 'string' 
        ? parseFloat(fee.lineAmount.replace(/[^0-9.-]/g, '')) || 0
        : parseFloat(fee.lineAmount) || 0
      return total + amount
    }, 0)
  }
  
  // Use live exchange rate from estimate data (required)
  const exchangeRate = estimateData?.exchangeRate
  
  if (!exchangeRate && estimateData) {
    console.error('No exchange rate provided in estimate data')
  }
  
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{
          padding: '16px',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          backgroundColor: '#f8f9fa'
        }}>
          <h2 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>Merged Customs & Brokerage Cost Estimate</h2>
          
          {/* Selected Shipments Summary */}
          <div style={{
            padding: '12px',
            backgroundColor: '#e2f3ff',
            border: '1px solid #b8daff',
            borderRadius: '4px',
            marginBottom: '16px'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              fontSize: '14px',
              fontWeight: 'bold',
              color: '#004085'
            }}>
              <span>Selected Shipments ({selectedShipments.length}):</span>
              <div style={{ textAlign: 'right' }}>
                <div>References: {selectedShipments.map(s => s.reference).join(', ')}</div>
                <div>{totalSkus} SKUs, {totalItems} units</div>
              </div>
            </div>
          </div>

          {/* Total Shipment Value */}
          {estimateData && (
            <div style={{
              padding: '12px',
              backgroundColor: '#e2f3ff',
              border: '1px solid #b8daff',
              borderRadius: '4px',
              marginBottom: '16px'
            }}>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                fontSize: '14px',
                fontWeight: 'bold',
                color: '#004085'
              }}>
                <span>Total Shipment Value:</span>
                <div style={{ textAlign: 'right' }}>
                  <div>
                    {formatCurrencyCAD(estimateData.summary?.total_value_cad || 0)} CAD
                  </div>
                  <div>
                    {exchangeRate 
                      ? formatCurrencyUSD((estimateData.summary?.total_value_cad || 0) * exchangeRate) + ' USD'
                      : 'Exchange rate unavailable'
                    }
                  </div>
                </div>
              </div>
            </div>
          )}

          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <div>
              <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>
                Customs & Brokerage Cost Estimate (USD)
              </h4>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
                Excludes freight shipping and YYZ Prep service fees
              </p>
            </div>
            {!estimateData && (
              <button
                onClick={onGenerate}
                disabled={loading}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  backgroundColor: loading ? '#6c757d' : '#007bff',
                  color: '#fff',
                  borderRadius: '4px',
                  fontSize: '14px',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Calculating...' : 'Get Estimate'}
              </button>
            )}
          </div>
          
          {loading && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#666'
            }}>
              <div style={{ fontSize: '18px', marginBottom: '10px' }}>Calculating merged estimate...</div>
              <div>Please wait while we process your shipments and calculate estimates.</div>
            </div>
          )}
          
          {estimateData && (
            <div>
              {/* Customs Fees */}
              {estimateData.customs_fees && estimateData.customs_fees.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h5 style={{ 
                    margin: '0 0 8px 0', 
                    fontSize: '14px', 
                    fontWeight: 'bold',
                    color: '#495057'
                  }}>
                    Customs & Duty Fees (USD)
                  </h5>
                  <div style={{
                    backgroundColor: '#fff',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px'
                  }}>
                    {estimateData.customs_fees.map((fee: any, index: number) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          borderBottom: index < estimateData.customs_fees.length - 1 ? '1px solid #e9ecef' : 'none',
                          fontSize: '13px'
                        }}
                      >
                        <span>{fee.lineDescription}</span>
                        <span style={{ fontWeight: 'bold' }}>
                          ${(typeof fee.lineAmount === 'string' 
                            ? parseFloat(fee.lineAmount.replace(/[^0-9.-]/g, '')) 
                            : parseFloat(fee.lineAmount) || 0
                          ).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      backgroundColor: '#e9ecef',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>Customs Total:</span>
                        <span style={{ fontSize: '12px', fontStyle: 'italic', fontWeight: 'normal' }}>
                          {estimateData.summary?.total_value_cad > 0 
                            ? `${((calculateTotal(estimateData.customs_fees) / ((estimateData.summary?.total_value_cad || 0) * exchangeRate)) * 100).toFixed(2)}% of shipment value`
                            : ''
                          }
                        </span>
                      </div>
                      <span>{formatCurrencyUSD(calculateTotal(estimateData.customs_fees))}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* ClearIt Fees */}
              {estimateData.clearit_fees && estimateData.clearit_fees.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h5 style={{ 
                    margin: '0 0 8px 0', 
                    fontSize: '14px', 
                    fontWeight: 'bold',
                    color: '#495057'
                  }}>
                    Brokerage Fees (USD)
                  </h5>
                  <div style={{
                    backgroundColor: '#fff',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px'
                  }}>
                    {estimateData.clearit_fees.map((fee: any, index: number) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          borderBottom: index < estimateData.clearit_fees.length - 1 ? '1px solid #e9ecef' : 'none',
                          fontSize: '13px'
                        }}
                      >
                        <span>
                          {fee.lineDescription}
                          {fee.note && <span style={{ color: '#666', fontSize: '11px', marginLeft: '6px' }}>{fee.note}</span>}
                        </span>
                        <span style={{ fontWeight: 'bold' }}>
                          ${(typeof fee.lineAmount === 'string' 
                            ? parseFloat(fee.lineAmount.replace(/[^0-9.-]/g, '')) 
                            : parseFloat(fee.lineAmount) || 0
                          ).toFixed(2)}
                        </span>
                      </div>
                    ))}
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      backgroundColor: '#e9ecef',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}>
                      <span>Brokerage Total:</span>
                      <span>{formatCurrencyUSD(calculateTotal(estimateData.clearit_fees))}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Grand Total */}
              <div style={{
                padding: '12px',
                backgroundColor: '#d4edda',
                border: '1px solid #c3e6cb',
                borderRadius: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#155724',
                marginBottom: '12px'
              }}>
                <span>Total Customs & Brokerage Cost (USD):</span>
                <span>
                  {formatCurrencyUSD(
                    calculateTotal(estimateData.customs_fees || []) + 
                    calculateTotal(estimateData.clearit_fees || [])
                  )}
                </span>
              </div>

              {/* Per Unit Costs */}
              <div style={{
                padding: '12px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                marginBottom: '16px'
              }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    Per Unit Cost (USD):
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#495057' }}>
                    {formatCurrencyUSD(
                      (calculateTotal(estimateData.customs_fees || []) + calculateTotal(estimateData.clearit_fees || [])) / 
                      (estimateData.summary?.total_items || 1)
                    )}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                    Per Unit Cost (CAD):
                  </div>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#495057' }}>
                    {formatCurrencyCAD(
                      ((calculateTotal(estimateData.customs_fees || []) + calculateTotal(estimateData.clearit_fees || [])) / 
                      (estimateData.summary?.total_items || 1)) / exchangeRate
                    )}
                  </div>
                </div>
              </div>

              {/* Shipments Breakdown */}
              {estimateData.shipments && estimateData.shipments.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <h5 style={{ 
                    margin: '0 0 8px 0', 
                    fontSize: '14px', 
                    fontWeight: 'bold',
                    color: '#495057'
                  }}>
                    Shipments Included
                  </h5>
                  <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                    {estimateData.shipments.map((shipment: any, index: number) => (
                      <div key={index} style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '6px 8px',
                        borderBottom: index < estimateData.shipments.length - 1 ? '1px solid #e9ecef' : 'none',
                        fontSize: '12px',
                        backgroundColor: '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: '2px',
                        marginBottom: '2px'
                      }}>
                        <span style={{ fontWeight: 'bold' }}>{shipment.reference}</span>
                        <span>{shipment.total_items || 0} items • {formatCurrencyCAD(shipment.total_value_cad || 0)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{
                padding: '8px',
                fontSize: '12px',
                color: '#6c757d',
                fontStyle: 'italic'
              }}>
                * <strong>IMPORTANT:</strong> This estimate ONLY includes customs duties and brokerage fees.<br/>
                * <strong>NOT INCLUDED:</strong> Freight shipping costs, YYZ Prep service fees, or other logistics charges.<br/>
                * All costs displayed in USD. COGS values converted from CAD to USD at {exchangeRate.toFixed(4)} exchange rate.<br/>
                * Per-clearance fees (MPF, PGA, Brokerage) charged once per consolidated clearance.<br/>
                * Actual costs may vary based on final shipment details and customs review.
              </div>
            </div>
          )}
        </div>
        
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '10px 20px',
              border: '1px solid #ddd',
              backgroundColor: '#fff',
              color: '#666',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// Multi-Client Invoice Dialog (Staff Only)
interface MultiClientInvoiceDialogProps {
  primaryClient: { id: number; name: string }
  secondaryClient: { id: number; name: string; email: string }
  primaryShipments: Shipment[]
  secondaryShipments: Shipment[]
  loading: boolean
  onGenerate: (params: {
    importerClientId: number
    password: string
    ltlType: 'direct_amazon' | 'pattern_middle_mile' | 'ups_buffalo'
    brokerType: 'clearit' | 'acb' | 'jas'
    fcInfo?: any
  }) => void
  onCancel: () => void
}

function MultiClientInvoiceDialog({
  primaryClient,
  secondaryClient,
  primaryShipments,
  secondaryShipments,
  loading,
  onGenerate,
  onCancel
}: MultiClientInvoiceDialogProps) {
  const [password, setPassword] = useState('')
  const [brokerType, setBrokerType] = useState<'clearit' | 'acb' | 'jas'>('clearit')
  const [ltlType, setLtlType] = useState<'direct_amazon' | 'pattern_middle_mile' | 'ups_buffalo'>('pattern_middle_mile')
  const [fcId, setFcId] = useState('')
  const [fcAddress, setFcAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: ''
  })
  const [importerClientId, setImporterClientId] = useState<number>(primaryClient.id)
  const [cachedMinutes, setCachedMinutes] = useState(0)

  // Load cached password on mount
  useEffect(() => {
    const cached = passwordCache.get()
    if (cached) {
      setPassword(cached)
      setCachedMinutes(passwordCache.getRemainingMinutes())
    }
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!password.trim()) return

    // Cache the password for 2 hours
    passwordCache.set(password)

    const fcInfo = ltlType === 'direct_amazon' && fcId.trim()
      ? { fcId: fcId.trim(), fcAddress }
      : undefined

    onGenerate({
      importerClientId,
      password,
      ltlType,
      brokerType,
      fcInfo
    })
  }

  const totalPrimaryItems = primaryShipments.reduce((sum, s) => sum + (s.itemCount || 0), 0)
  const totalSecondaryItems = secondaryShipments.reduce((sum, s) => sum + (s.itemCount || 0), 0)
  const totalShipments = primaryShipments.length + secondaryShipments.length

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '700px',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ padding: '24px' }}>
          <h2 style={{ margin: '0 0 20px 0' }}>Generate Multi-Client Combined Invoice</h2>

          {/* Multi-Client Summary */}
          <div style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #0ea5e9',
            padding: '16px',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#0369a1' }}>
              Merging Shipments from 2 Clients
            </h3>

            {/* Primary Client */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px',
              padding: '8px',
              backgroundColor: '#e8f5e9',
              borderRadius: '4px'
            }}>
              <span style={{
                backgroundColor: '#4caf50',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px'
              }}>Primary</span>
              <strong>{primaryClient.name}</strong>
              <span style={{ marginLeft: 'auto', color: '#666', fontSize: '13px' }}>
                {primaryShipments.length} shipments, {totalPrimaryItems} items
              </span>
            </div>

            {/* Secondary Client */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px',
              backgroundColor: '#e3f2fd',
              borderRadius: '4px'
            }}>
              <span style={{
                backgroundColor: '#2196f3',
                color: '#fff',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px'
              }}>Secondary</span>
              <strong>{secondaryClient.name}</strong>
              <span style={{ marginLeft: 'auto', color: '#666', fontSize: '13px' }}>
                {secondaryShipments.length} shipments, {totalSecondaryItems} items
              </span>
            </div>

            <div style={{ marginTop: '12px', fontSize: '14px', color: '#0369a1', textAlign: 'center' }}>
              <strong>Total: {totalShipments} shipments, {totalPrimaryItems + totalSecondaryItems} items</strong>
            </div>
          </div>

          {/* Importer Selection */}
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            padding: '16px',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#856404' }}>
              Select Importer (Business Info on Invoice)
            </h3>
            <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#856404' }}>
              Which client's business information should appear as the importer on the invoice?
            </p>

            <div style={{ display: 'flex', gap: '16px' }}>
              <label style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                border: importerClientId === primaryClient.id ? '2px solid #4caf50' : '1px solid #dee2e6',
                borderRadius: '6px',
                cursor: 'pointer',
                backgroundColor: importerClientId === primaryClient.id ? '#e8f5e9' : '#fff'
              }}>
                <input
                  type="radio"
                  name="importerClient"
                  checked={importerClientId === primaryClient.id}
                  onChange={() => setImporterClientId(primaryClient.id)}
                />
                <div>
                  <div style={{ fontWeight: 'bold' }}>{primaryClient.name}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Primary Client</div>
                </div>
              </label>

              <label style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px',
                border: importerClientId === secondaryClient.id ? '2px solid #2196f3' : '1px solid #dee2e6',
                borderRadius: '6px',
                cursor: 'pointer',
                backgroundColor: importerClientId === secondaryClient.id ? '#e3f2fd' : '#fff'
              }}>
                <input
                  type="radio"
                  name="importerClient"
                  checked={importerClientId === secondaryClient.id}
                  onChange={() => setImporterClientId(secondaryClient.id)}
                />
                <div>
                  <div style={{ fontWeight: 'bold' }}>{secondaryClient.name}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Secondary Client</div>
                </div>
              </label>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* LTL Type Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                LTL Type
              </label>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="ltlType"
                    value="pattern_middle_mile"
                    checked={ltlType === 'pattern_middle_mile'}
                    onChange={(e) => setLtlType(e.target.value as any)}
                  />
                  Pattern Middle Mile
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="ltlType"
                    value="direct_amazon"
                    checked={ltlType === 'direct_amazon'}
                    onChange={(e) => setLtlType(e.target.value as any)}
                  />
                  Direct to Amazon
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="ltlType"
                    value="ups_buffalo"
                    checked={ltlType === 'ups_buffalo'}
                    onChange={(e) => setLtlType(e.target.value as any)}
                  />
                  UPS Buffalo
                </label>
              </div>
            </div>

            {/* FC Info for Direct to Amazon */}
            {ltlType === 'direct_amazon' && (
              <div style={{
                padding: '16px',
                backgroundColor: '#f8f9fa',
                borderRadius: '6px',
                marginBottom: '20px'
              }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  Fulfillment Center ID
                </label>
                <input
                  type="text"
                  value={fcId}
                  onChange={(e) => setFcId(e.target.value)}
                  placeholder="e.g., MDW2"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginBottom: '12px'
                  }}
                />

                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  FC Address
                </label>
                <input
                  type="text"
                  value={fcAddress.street}
                  onChange={(e) => setFcAddress({ ...fcAddress, street: e.target.value })}
                  placeholder="Street Address"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    marginBottom: '8px'
                  }}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                  <input
                    type="text"
                    value={fcAddress.city}
                    onChange={(e) => setFcAddress({ ...fcAddress, city: e.target.value })}
                    placeholder="City"
                    style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <input
                    type="text"
                    value={fcAddress.state}
                    onChange={(e) => setFcAddress({ ...fcAddress, state: e.target.value })}
                    placeholder="State"
                    style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                  <input
                    type="text"
                    value={fcAddress.zip}
                    onChange={(e) => setFcAddress({ ...fcAddress, zip: e.target.value })}
                    placeholder="ZIP"
                    style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                  />
                </div>
              </div>
            )}

            {/* Broker Type Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Customs Broker
              </label>
              <div style={{ display: 'flex', gap: '12px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="brokerType"
                    value="clearit"
                    checked={brokerType === 'clearit'}
                    onChange={(e) => setBrokerType(e.target.value as any)}
                  />
                  ClearIT USA
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="brokerType"
                    value="acb"
                    checked={brokerType === 'acb'}
                    onChange={(e) => setBrokerType(e.target.value as any)}
                  />
                  ACB (Accurate Customs Broker)
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="brokerType"
                    value="jas"
                    checked={brokerType === 'jas'}
                    onChange={(e) => setBrokerType(e.target.value as any)}
                  />
                  JAS Forwarding (Lynn E. Reed)
                </label>
              </div>
            </div>

            {/* Password */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                Invoice Generation Password
                {cachedMinutes > 0 && (
                  <span style={{ fontWeight: 'normal', color: '#28a745', marginLeft: '8px', fontSize: '12px' }}>
                    (cached for {cachedMinutes} min)
                  </span>
                )}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: cachedMinutes > 0 ? '1px solid #28a745' : '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: cachedMinutes > 0 ? '#f8fff8' : '#fff'
                }}
              />
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                type="button"
                onClick={onCancel}
                disabled={loading}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #ddd',
                  backgroundColor: '#fff',
                  color: '#666',
                  borderRadius: '4px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || !password.trim()}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  backgroundColor: loading || !password.trim() ? '#ccc' : '#28a745',
                  color: '#fff',
                  borderRadius: '4px',
                  cursor: loading || !password.trim() ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                {loading ? 'Generating...' : 'Generate Multi-Client Combined Invoice'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
